import React, { Fragment, useEffect, useState, useRef } from 'react';
import { Table, Spin, Alert, DatePicker, Select, message, Button } from 'antd';
import Card from '../../../components/bootstrap/card';
import { Col, Row } from 'react-bootstrap';
import { Excel } from 'antd-table-saveas-excel';
import { getAdvLicImportExportReportService } from '../../../services/AdvLicImportExportReportService';
import { getReportSummaryData, getAllAdminIECsService, GetLicenseNoService } from '../../../services/FileDataService';
import { getBRCorelationService } from '../../../services/MasterService';
import { getTodaysDate, formatToTwoDecimals } from '../../../utilities/utils';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';

import useColumnVisibility from '../../../hooks/useColumnVisibility';
import ColumnSelector from '../../../components/common/ColumnSelector';

const { RangePicker } = DatePicker;

const AdvLicImportExportReport = () => {
  const location = useLocation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading2, setIsLoading2] = useState(false);
  const [isLoading3, setIsLoading3] = useState(false);
  const [summaryDashboardData, setSummaryDashboardData] = useState([]);
  const [oldSummaruDashboardData, setOldSummaryDashboardData] = useState([]);
  const [IECOptions, setIECOptions] = useState([]);
  const userRole = localStorage.getItem('role');
  const [selectedIEC, setSelectedIEC] = useState(
    localStorage.getItem('iec_no')
  );
  const [selectedIECName, setSelectedIECName] = useState(localStorage.getItem('name'))
  const tableRef = useRef(null);
  const [isValid, setIsValid] = useState(true); // Validation state
  const [billNoOptions, setBillNoOptions] = useState([]); // Options for Auth No dropdown
  const [selectedAuthNos, setSelectedAuthNos] = useState([]); // multi-select auth numbers
  const [branchCodeNames, setBranchCodeNames] = useState([]);
  const [selectedBranchCodes, setSelectedBranchCodes] = useState([]);

  const currentYear = dayjs().year();
  const defaultFrom = dayjs(`${currentYear}-01-01`);
  const defaultTo = dayjs(`${currentYear}-12-31`);
  const [dates, setDates] = useState(() => {
    if (location.state?.passedDates?.length === 2) {
      return [
        dayjs(location.state.passedDates[0]),
        dayjs(location.state.passedDates[1]),
      ];
    }
    return [defaultFrom, defaultTo];
  });

  // Fetch license numbers immediately for the selected IEC
  const fetchLicenseNumbers = async (iec) => {
    const targetIEC = iec || selectedIEC;
    if (!targetIEC) return;
    try {
      const token = localStorage.getItem('token');
      const res = await GetLicenseNoService(token, targetIEC, 'ADV_AUTH');
      const list = res?.data || (Array.isArray(res) ? res : []);
      if (list.length > 0) {
        const options = list.map((item, index) => ({
          value: typeof item === 'object' ? (item.license_number || item.auth_number) : String(item),
          label: typeof item === 'object' ? (item.license_number || item.auth_number) : String(item),
          key: index,
        }));
        setBillNoOptions(options);
      }
    } catch (error) {
      console.error('Error fetching license numbers:', error);
    }
  };

  useEffect(() => {
    // On mount: ONLY fetch IEC dropdown. No report data until Submit is clicked.
    getAdminIEC();
    // For admin/user (single IEC), load licenses and branch codes immediately
    if (userRole === 'admin' || userRole === 'user') {
      fetchLicenseNumbers(selectedIEC);
      getBRCorelationService(selectedIEC).then((response) => {
        setBranchCodeNames(response || []);
      });
    }
  }, []);

  // When IEC changes (superadmin selects an IEC), load its licenses & branch codes
  useEffect(() => {
    if (selectedIEC) {
      setBillNoOptions([]);      // clear stale licenses
      setSelectedAuthNos([]);   // clear selections
      fetchLicenseNumbers(selectedIEC);
      getBRCorelationService(selectedIEC).then((response) => {
        setBranchCodeNames(response || []);
      });
    }
  }, [selectedIEC]);

  const fetchData = async () => {
    setLoading(true);
    const filters = {
      bill_no: selectedAuthNos.length > 0 ? selectedAuthNos : null,
      br_code: selectedBranchCodes.length > 0 ? selectedBranchCodes : null,
    };
    try {
      const response = await getAdvLicImportExportReportService(selectedIEC, filters);
      setData(response);
    } catch (error) {
      console.error('Error fetching Adv Lic Import Export report data:', error);
      setError('Error fetching data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getTodayDate = () => {
    const today = new Date();
    // Extract day, month, and year components
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0'); // January is 0!
    const year = today.getFullYear();
    const formattedDate = `${day}-${month}-${year}`;
    return formattedDate
  }

  const formatDateToDDMMYY = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return '';
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const calculateDaysDifference = (date1, date2) => {
    const diffTime = Math.abs(new Date(date2) - new Date(date1));
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // ─── TABLE STYLES ────────────────────────────────────────────────────────────
  const tableStyles = `
    .adv-lic-summary-table .ant-table-thead > tr > th,
    .adv-lic-old-summary-table .ant-table-thead > tr > th,
    .adv-lic-table .ant-table-thead > tr > th {
      white-space: normal !important;
      word-break: break-word !important;
      overflow: visible !important;
      text-overflow: unset !important;
      vertical-align: middle !important;
      line-height: 1.3 !important;
      padding-top: 8px !important;
      padding-bottom: 8px !important;
    }
    .adv-lic-summary-table .ant-table-tbody > tr > td,
    .adv-lic-old-summary-table .ant-table-tbody > tr > td,
    .adv-lic-table .ant-table-tbody > tr > td {
      white-space: nowrap;
    }
    .adv-lic-summary-table .ant-table-container,
    .adv-lic-old-summary-table .ant-table-container,
    .adv-lic-table .ant-table-container {
      overflow-x: auto !important;
    }
    .adv-lic-summary-table .ant-table-header,
    .adv-lic-old-summary-table .ant-table-header,
    .adv-lic-table .ant-table-header {
      overflow: hidden !important;
    }
    .adv-lic-summary-table .ant-table-sticky-holder,
    .adv-lic-old-summary-table .ant-table-sticky-holder,
    .adv-lic-table .ant-table-sticky-holder {
      overflow: hidden !important;
    }
  `;

  const columns = [
    { title: 'BUSINESS UNIT', dataIndex: 'business_unit', key: 'business_unit', header: 'BUSINESS UNIT', width: 160 , fixed: 'left' },
    { title: 'PLANT NAME', dataIndex: 'plant_name', key: 'plant_name', header: 'PLANT NAME', width: 150 , fixed: 'left' },
    { title: 'ADV. LIC. NO.', dataIndex: 'auth_number', key: 'auth_number', header: 'ADV. LIC. NO.', width: 160 },
    {
      title: 'DATE',
      dataIndex: 'import_date',
      key: 'import_date',
      header: 'DATE',
      width: 100,
      render: (text, record) => {
        if (record.import_date) {
          return formatDateToDDMMYY(record.import_date);
        }
      }
    },
    { title: 'EXPORT ITEM SR. NO. AS PER LIC.', dataIndex: 'export_sr_no', key: 'export_sr_no', header: 'EXPORT ITEM SR. NO. AS PER LIC.', align: 'right', width: 320 },
    {
      title: 'AUTH. DESCRIPTION',
      dataIndex: 'item_name',
      key: 'item_name',
      header: 'AUTH. DESCRIPTION',
      width: 200,
      render: (text, record) => `${record.item_name} ${record.item_description}`
    },
    { title: 'ATHO. QTY.', dataIndex: 'quantity', key: 'quantity', header: 'ATHO. QTY.', align: 'right', width: 140 },
    { title: 'ATHO. UOM', dataIndex: 'uom', key: 'uom', header: 'ATHO. UOM', width: 140 },
    {
      title: 'VALUE IN INR',
      dataIndex: 'fob_for_value',
      key: 'fob_for_value',
      header: 'VALUE IN INR',
      align: 'right',
      width: 155,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    { title: 'IEC/BR CODE', dataIndex: 'iec_br', key: 'iec_br', header: 'IEC/BR CODE', width: 150 },
    { title: 'REF. CODE', dataIndex: 'reference_code', key: 'reference_code', header: 'REF. CODE', width: 125 },
    { title: 'SHB NO.', dataIndex: 'sb_no', key: 'sb_no', header: 'SHB NO.', width: 115 },
    { title: 'SHB DT.', dataIndex: 'sb_date', key: 'sb_date', header: 'SHB DT.', width: 110 },
    { title: 'HSN CODE', dataIndex: 'hs_cd', key: 'hs_cd', header: 'HSN CODE', width: 115 },
    { title: 'DECRIPTION', dataIndex: 'descn_of_export_item', key: 'descn_of_export_item', header: 'DECRIPTION', width: 130 },
    { title: 'EXPORT ITEM SR. NO.', dataIndex: 'exp_s_no', key: 'exp_s_no', header: 'EXPORT ITEM SR. NO.', align: 'right', width: 210 },
    { title: 'QTY.', dataIndex: 'expqty', key: 'expqty', header: 'QTY.', align: 'right', width: 90 },
    { title: 'IMPORT ITEM SR. NO.', dataIndex: 'imp_s_no', key: 'imp_s_no', header: 'IMPORT ITEM SR. NO.', align: 'right', width: 210 },
    { title: 'QTY. USED', dataIndex: 'impqt', key: 'impqt', header: 'QTY. USED', align: 'right', width: 130 },
    { title: 'UOM', dataIndex: 'uqc_', key: 'uqc_', header: 'UOM', width: 80 },
  ];

  const { visibleKeys, setVisibleKeys, filteredColumns, resetToDefault } =
    useColumnVisibility('AdvLicImportExportReport', columns);

  const oldDashboardReportSummaryColumns = [
    {
      title: 'ADV. LIC. NO.',
      dataIndex: 'auth_number',
      key: 'auth_number',
      header: 'ADV. LIC. NO.',
      width: 160
    },
    {
      title: 'DATE',
      dataIndex: 'import_date',
      key: 'import_date',
      header: 'DATE',
      width: 100
    },
    {
      title: 'IMPORT ITEM SR. NO.',
      dataIndex: 'import_sr_no',
      key: 'import_sr_no',
      header: 'IMPORT ITEM SR. NO.',
      align: 'right',
      width: 210
    },
    {
      title: 'IMPORT QTY. USED TOTAL',
      dataIndex: 'imp_qty_total',
      key: 'imp_qty_total',
      header: 'IMPORT QTY. USED TOTAL',
      align: 'right',
      width: 245
    },
    {
      title: 'IMP QTY. UOM',
      dataIndex: 'imp_qty_uom',
      key: 'imp_qty_uom',
      header: 'IMP QTY. UOM',
      width: 150
    },
    {
      title: 'ACTUAL QTY. IMPORTED TOTAL',
      dataIndex: 'actual_imported_qty',
      key: 'actual_imported_qty',
      header: 'ACTUAL QTY. IMPORTED TOTAL',
      align: 'right',
      width: 285
    },
    {
      title: 'UOM (AS PER AUTH.)',
      dataIndex: 'uom_as_per_auth',
      key: 'uom_as_per_auth',
      header: 'UOM (AS PER AUTH.)',
      width: 200
    },
    {
      title: 'DIFFERENCE',
      dataIndex: 'difference',
      key: 'difference',
      header: 'DIFFERENCE',
      align: 'right',
      width: 140
    },
  ];


  const dashboardReportSummaryColumns = [
    { title: 'ADV. LIC. NO.', dataIndex: 'auth_number', key: 'auth_number', header: 'ADV. LIC. NO.', width: 160 },
    { title: 'DATE', dataIndex: 'import_date', key: 'import_date', header: 'DATE', width: 100 },
    { title: 'EXP. SR. NO.', dataIndex: 'export_sr_no', key: 'export_sr_no', header: 'EXP. SR. NO.', width: 150 },
    { title: 'EXP. DESCRIPTION', dataIndex: 'exp_item_name', key: 'exp_item_name', header: 'EXP. DESCRIPTION', width: 180 },
    { title: 'EXP. QTY. AS PER AUTH.', dataIndex: 'exp_qty_per_auth', key: 'exp_qty_per_auth', header: 'EXP. QTY. AS PER AUTH.', width: 235 },
    { title: 'EXP. UOM', dataIndex: 'exp_uom', key: 'exp_uom', header: 'EXP. UOM', width: 110 },
    { title: 'ACTUAL EXP. MADE', dataIndex: 'actual_exp_made', key: 'actual_exp_made', header: 'ACTUAL EXP. MADE', width: 180 },
    { title: 'EO PENDING W.R.T. AUTHO.', dataIndex: 'eo_pending_wrt_auth', key: 'eo_pending_wrt_auth', header: 'EO PENDING W.R.T. AUTHO.', width: 260 },
    { title: 'UOM', dataIndex: 'exp_uom1', key: 'exp_uom1', header: 'UOM', width: 80 },
    { title: 'EXP. / IMPORT ITEM SR. NO.', dataIndex: 'exp_imp_item_srno', key: 'exp_imp_item_srno', header: 'EXP. / IMPORT ITEM SR. NO.', width: 285 },
    { title: 'IMPORT ITEM SR. NO.', dataIndex: 'import_sr_no', key: 'import_sr_no', header: 'IMPORT ITEM SR. NO.', width: 210 },
    { title: 'IMPORT ITEM DESCRIPTION', dataIndex: 'imp_item_name', key: 'imp_item_name', header: 'IMPORT ITEM DESCRIPTION', width: 250 },
    { title: 'IMP. QTY. AS PER LIC.', dataIndex: 'imp_qty_per_auth', key: 'imp_qty_per_auth', header: 'IMP. QTY. AS PER LIC.', width: 225, render: (text, record) => { return formatToTwoDecimals(text) } },
    { title: 'IMP. UOM', dataIndex: 'imp_uom', key: 'imp_uom', header: 'IMP. UOM', width: 110 },
    { title: 'PER UNIT REQD.', dataIndex: 'per_unit_required', key: 'per_unit_required', header: 'PER UNIT REQD.', width: 170 },
    { title: 'ACTUAL IMPORTS MADE', dataIndex: 'actual_imports_made', key: 'actual_imports_made', header: 'ACTUAL IMPORTS MADE', width: 205 },
    { title: 'IMPORT BAL. W.R.T. AUTH. QTY.', dataIndex: 'import_bal_wrt_auth_qty', key: 'import_bal_wrt_auth_qty', header: 'IMPORT BAL. W.R.T. AUTH. QTY.', width: 310 },
    { title: 'UOM', dataIndex: 'imp_uom1', key: 'imp_uom1', header: 'UOM', width: 80 },
    { title: 'PROPORTIONATE EXP. BAL. (W.R.T IMP. MADE)', dataIndex: 'eo_pending_wrt_actual_imp', key: 'eo_pending_wrt_actual_imp', header: 'PROPORTIONATE EXP. BAL. (W.R.T IMP. MADE)', width: 415, render: (text, record) => { return formatToTwoDecimals(text) } },
    { title: 'PROPORTIONATE IMP. BAL. (W.R.T EXP. MADE)', dataIndex: 'import_bal_wrt_eo_made', key: 'import_bal_wrt_eo_made', header: 'PROPORTIONATE IMP. BAL. (W.R.T EXP. MADE)', width: 415 },
    { title: 'EOP DATE', dataIndex: 'eop_date', key: 'eop_date', header: 'EOP DATE', width: 110 },
    { title: 'BAL. EOP', dataIndex: 'bal_eop', key: 'bal_eop', header: 'BAL. EOP', width: 110 },
    { title: 'IMP. VALIDITY', dataIndex: 'imp_validity', key: 'imp_validity', header: 'IMP. VALIDITY', width: 160 },
    { title: 'BAL. DAYS', dataIndex: 'bal_days', key: 'bal_days', header: 'BAL. DAYS', width: 120 },
    { title: 'LIC. REMARK', dataIndex: 'lic_remark', key: 'lic_remark', header: 'LIC. REMARK', width: 140 },
  ];

  const getSummaryDashboardOld = async () => {
    const filters = {
      bill_no: selectedAuthNos.length > 0 ? selectedAuthNos : null,
      br_code: selectedBranchCodes.length > 0 ? selectedBranchCodes : null,
    };
    try {
      setIsLoading3(true);
      const response = await getReportSummaryData('AdvLic-ExcessImp-V1', filters, selectedIEC);
      if (response?.data) {
        setOldSummaryDashboardData(response.data);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading3(false);
    }
  };

  const getSummaryDashboard = async () => {
    const filters = {
      bill_no: selectedAuthNos.length > 0 ? selectedAuthNos : null,
      br_code: selectedBranchCodes.length > 0 ? selectedBranchCodes : null,
    };
    try {
      setIsLoading2(true);
      const response = await getReportSummaryData('AdvLic-ExcessImp', filters, selectedIEC);
      if (response?.data) {
        setSummaryDashboardData(response.data);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading2(false);
    }
  };


  const getDateOnly = (dateString) => {
    if (dateString && dateString.length !== 0) {
      const date = new Date(dateString);
      return date;
    }
    else {
      return '';
    }
  }
  const handleDateChange = (value) => {
    setDates(value || []);
    if (value && value.length === 2) {
      const from_date = value[0];
      const to_date = value[1];

      // Validate that both dates are selected
      if (!from_date || !to_date) {
        setIsValid(false);
        return;
      }

      // Validate that the duration is within 1 year
      const oneYearLater = from_date.add(1, 'year');
      if (to_date.isAfter(oneYearLater)) {
        message.error("The selected duration should not exceed 1 year.");
        setIsValid(false);
        return;
      }

      setIsValid(true);
    } else {
      setIsValid(false);
    }
  };
  const handleSubmit = () => {
    if (!selectedIEC) {
      message.error("Please select an IEC");
      return;
    }
    getSummaryDashboard();
    getSummaryDashboardOld();
    fetchData();
  };

  const getAdminIEC = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await getAllAdminIECsService(token);
      if (response?.data) {
        const options = response.data.map((item, index) => ({
          value: `${item.iec_no};${item.fullname}`,
          label: `${item.iec_no} (${item.fullname})`,
          key: index,
        }));
        setIECOptions(options);
      }
    } catch (err) {
      message.error("Failed to load IEC list");
    }
  };

  const handleIECSelection = (val) => {
    if (val) {
      const [iec_no, iec_name] = val.split(";");
      setSelectedIEC(iec_no);
      setSelectedIECName(iec_name);
    } else {
      setSelectedIEC(null);
      setSelectedIECName('');
    }
  };

  const handleBranchCodeSelection = (values) => {
    setSelectedBranchCodes(values);
  };

  const exportToExcel = async () => {
    let workbook = new ExcelJS.Workbook();
    let oldSummaryWorksheet = workbook.addWorksheet('Summary1');
    let summaryWorksheet = workbook.addWorksheet('Summary2');
    let worksheet = workbook.addWorksheet('Data');


    // Define columns for Excel export
    oldSummaryWorksheet.columns = oldDashboardReportSummaryColumns.map(col => ({
      header: col.header || col.title,
      key: col.dataIndex,
      width: 20
    }));
    // Apply styles to the header row (Row 1)
    oldSummaryWorksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true }; // Bold text
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFC000' }, // Yellow background
      };
      cell.alignment = { vertical: 'middle' }; // Center align
    });

    oldSummaruDashboardData.forEach((obj) => {
      oldSummaryWorksheet.addRow({
        ...obj,
        import_date: getDateOnly(obj.import_date),
        actual_imp_qty: obj.actual_imp_qty ? parseFloat(obj.actual_imp_qty) : '',
        used_for_exp_qty: obj.used_for_exp_qty ? parseFloat(obj.used_for_exp_qty) : '',
      })
    })

    // Define columns for Excel export
    summaryWorksheet.columns = filteredSummaryColumns.map(col => ({
      header: col.header || col.title,
      key: col.dataIndex,
      width: 20
    }));
    // Apply styles to the header row (Row 1)
    summaryWorksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true }; // Bold text
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFC000' }, // Yellow background
      };
      cell.alignment = { vertical: 'middle' }; // Center align
    });

    summaryDashboardData.forEach((obj) => {
      summaryWorksheet.addRow({
        ...obj,
        import_date: getDateOnly(obj.import_date),
        actual_imp_qty: obj.actual_imp_qty ? parseFloat(obj.actual_imp_qty) : '',
        used_for_exp_qty: obj.used_for_exp_qty ? parseFloat(obj.used_for_exp_qty) : '',
      })
    })

    worksheet.columns = filteredColumns.map(col => ({
      header: col.header || col.title,
      key: col.dataIndex,
      width: 20
    }));
    // Apply styles to the header row (Row 1)
    worksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true }; // Bold text
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFC000' }, // Yellow background
      };
      cell.alignment = { vertical: 'middle' }; // Center align
    });

    data.forEach((obj) => {
      worksheet.addRow({
        ...obj,
        import_date: getDateOnly(obj.import_date),
        sb_date: getDateOnly(obj.sb_date),
        quantity: obj.quantity ? parseFloat(obj.quantity) : '',
        fob_for_value: obj.fob_for_value ? parseFloat(obj.fob_for_value) : '',
        expqty: obj.expqty ? parseFloat(obj.expqty) : '',
        impqt: obj.impqt ? parseFloat(obj.impqt) : '',
      })

    });

    // Generate Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    // Use FileSaver to save the file
    saveAs(blob, `Lucrative's CIP DSS - Adv. Lic. Accountability with Excess Import Statement as on ${getTodaysDate()} of ${selectedIEC}.xlsx`);
  };

  //  const exportToExcel = () => {
  //   const tableData = getDataToRender();
  //   const ws = XLSX.utils.json_to_sheet(tableData);
  //   const ws1 = XLSX.utils.json_to_sheet(summaryDashboardData);
  //   const wb = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(wb, ws1, 'Sheet1');
  //   XLSX.utils.book_append_sheet(wb, ws, 'Sheet2');
  //   XLSX.writeFile(wb, 'AdvLicAccExcessImportStmt.xlsx');
  // };

  // const getDataToRender = () => {
  //   if (tableRef.current) {
  //     return data;
  //   }
  //   return [];
  // };

  const filteredSummaryColumns = dashboardReportSummaryColumns.filter((col) => {
    const key = String(col.dataIndex || col.key);
    const isDefinedInDetail = columns.some((c) => String(c.dataIndex || c.key) === key);
    return isDefinedInDetail ? visibleKeys.includes(key) : true;
  });

  return (
    <Fragment>
      <style>{tableStyles}</style>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between">
              <div className="header-title">
                <h4 className="card-title">Lucrative's CIP DSS - Adv. Lic. Accountability with Excess Import Statement as on {getTodayDate()} of {selectedIECName}</h4>
              </div>
              <div className="d-flex align-items-center gap-2">
                <ColumnSelector
                  allColumns={columns}
                  visibleKeys={visibleKeys}
                  setVisibleKeys={setVisibleKeys}
                  onReset={resetToDefault}
                />
                <button
                  className="btn btn-primary cip-btn-submit"
                  onClick={selectedAuthNos.length === 0 ? undefined : exportToExcel}
                  style={{ cursor: (loading || selectedAuthNos.length === 0) ? 'not-allowed' : 'pointer' }}
                >
                  Export to Excel
                </button>
              </div>
            </Card.Header>
            <Card.Body className="d-flex justify-content-start align-items-center gap-2 ps-4">
              {(userRole === 'superadmin' || userRole === 'group_head') && (
                <Select
                  showSearch
                  placeholder="Select IEC"
                  value={selectedIEC ? (IECOptions.find(opt => opt.value.split(';')[0] === selectedIEC)?.value || selectedIEC) : undefined}
                  onChange={handleIECSelection}
                  allowClear
                  options={IECOptions}
                  style={{ width: '300px' }}
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                />
              )}
              {/* <DatePicker.RangePicker value={dates} onChange={handleDateChange} allowClear /> */}
              <Select
                mode="multiple"
                showSearch
                placeholder="Select Auth No(s)"
                value={selectedAuthNos}
                allowClear
                style={{ width: 280 }}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                onChange={(vals) => setSelectedAuthNos(vals || [])}
                options={billNoOptions}
                disabled={!selectedIEC}
              />
              <Select
                mode="multiple"
                allowClear
                style={{ width: 250 }}
                placeholder="Select Branch Code"
                onChange={handleBranchCodeSelection}
                options={branchCodeNames.map((item) => ({
                  value: item.branch_code,
                  label: `${item.branch_code} / ${item.plant_name} (${item.business_unit})`,
                }))}
              />
              <Button type="primary" className="cip-btn-submit"
                style={{ cursor: selectedAuthNos.length === 0 ? 'not-allowed' : 'pointer' }}
                onClick={selectedAuthNos.length === 0 ? undefined : handleSubmit}>
                Submit
              </Button>
            </Card.Body>
            <Card.Body className="px-0 report-page">
              <Table
                className="adv-lic-summary-table"
                columns={filteredSummaryColumns}
                dataSource={summaryDashboardData}
                pagination={false}
                loading={isLoading2}
                bordered
                scroll={{ x: 'max-content' }}
                
              />
              <hr />
              <Table
                className="adv-lic-old-summary-table"
                columns={oldDashboardReportSummaryColumns}
                dataSource={oldSummaruDashboardData}
                pagination={false}
                loading={isLoading3}
                bordered
                scroll={{ x: 'max-content' }}
                
              />
              <hr />
              <Table
                className="adv-lic-table"
                dataSource={data}
                columns={filteredColumns}
                ref={tableRef}
                loading={loading}
                bordered
                pagination={{ showTotal: (total) => `Total ${total} records`, showSizeChanger: true }}
                scroll={{ x: 'max-content' }}
                
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
};

export default AdvLicImportExportReport;
